import { useMemo } from "react"
import { PlusIcon, Trash2Icon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { SearchableSelect } from "@/components/ui/searchable-select"
import { cn } from "@/lib/utils"
import { syncNationalRouteStops, type NationalRouteStop, type NationalRouteStopKind } from "@/lib/national-route"

export interface NationalRouteOption {
  id: string
  code: string
  name: string
  km: number | null
  originCityName: string
  targetCityName: string
}

export interface NationalPartyOption {
  id: string
  name: string
  externalId?: string | null
  cityName?: string | null
  countryName?: string | null
}


interface NationalRouteEditorProps {
  senderId: string
  recipientId: string
  routeId: string
  routeStops: NationalRouteStop[]
  routeOptions: NationalRouteOption[]
  partyOptions: NationalPartyOption[]
  onRouteIdChange: (routeId: string) => void
  onRouteStopsChange: (routeStops: NationalRouteStop[]) => void
}

export function NationalRouteEditor({
  senderId,
  recipientId,
  routeId,
  routeStops,
  routeOptions,
  partyOptions,
  onRouteIdChange,
  onRouteStopsChange,
}: NationalRouteEditorProps) {
  const routeSelectOptions = useMemo(() => [
    { value: "__none__", label: "Usar ruta remitente / destinatario" },
    ...routeOptions.map(route => ({
      value: route.id,
      label: `${route.code} · ${route.name}`,
      sublabel: `${route.originCityName} / ${route.targetCityName}${route.km != null ? ` · ${route.km} km` : ""}`,
      keywords: `${route.code} ${route.name} ${route.originCityName} ${route.targetCityName}`,
    })),
  ], [routeOptions])

  const partySelectOptions = useMemo(() => [
    { value: "__none__", label: "Sin asignar" },
    ...partyOptions.map(party => ({
      value: party.id,
      label: party.name,
      sublabel: [
        party.externalId ? `ID: ${party.externalId}` : null,
        party.cityName,
        party.countryName,
      ].filter(Boolean).join(" / ") || undefined,
      keywords: party.externalId ?? undefined,
    })),
  ], [partyOptions])

  const displayStops = routeStops.length
    ? routeStops
    : syncNationalRouteStops(senderId, recipientId)

  function updateStop(index: number, partyId: string) {
    onRouteStopsChange(displayStops.map((stop, stopIndex) => (
      stopIndex === index ? { ...stop, partyId } : stop
    )))
  }

  function addIntermediateStop() {
    const destinationIndex = displayStops.findIndex(stop => stop.kind === "destination")
    const next = [...displayStops]
    const insertAt = destinationIndex >= 0 ? destinationIndex : next.length
    next.splice(insertAt, 0, { partyId: "", kind: "intermediate" })
    onRouteStopsChange(next)
  }

  function removeIntermediateStop(index: number) {
    onRouteStopsChange(displayStops.filter((_, stopIndex) => stopIndex !== index))
  }

  return (
    <>
      <div className="grid gap-1.5">
        <Label>Ruta</Label>
        <SearchableSelect
          options={routeSelectOptions}
          value={routeId || "__none__"}
          onValueChange={value => onRouteIdChange(value === "__none__" ? "" : value)}
          placeholder="Usar ruta remitente / destinatario"
          searchPlaceholder="Buscar por código, nombre o ciudades..."
        />
        <p className="text-[11px] text-muted-foreground">
          Si no se selecciona una ruta, se utiliza la conexión configurada entre el remitente y el destinatario.
        </p>
      </div>

      {senderId && recipientId && (
        <div className="grid gap-2 rounded-md border bg-background/60 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <Label>Paradas</Label>
              <p className="text-[11px] text-muted-foreground">
                Son informativas y no generan viajes por tramo.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 shrink-0 gap-1 text-xs"
              onClick={addIntermediateStop}
            >
              <PlusIcon className="size-3" /> Parada
            </Button>
          </div>

          <div className="space-y-0">
            {displayStops.map((stop, index) => {
              const party = partyOptions.find(option => option.id === stop.partyId)
              const isFixed = stop.kind === "origin" || stop.kind === "destination"
              const isLast = index === displayStops.length - 1
              return (
                <div key={`${index}-${stop.partyId || "empty"}`}>
                  <div className="grid py-1" style={{ gridTemplateColumns: "1.25rem 1fr auto" }}>
                    <div className="flex items-center justify-center">
                      <div className={cn(
                        "flex size-5 items-center justify-center rounded-full text-[9px] font-bold",
                        stop.kind === "origin"
                          ? "bg-emerald-100 text-emerald-700"
                          : stop.kind === "destination"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-orange-100 text-orange-700",
                      )}>
                        {stop.kind === "origin" ? "O" : stop.kind === "destination" ? "D" : index}
                      </div>
                    </div>
                    <div className="min-w-0 px-2">
                      {isFixed ? (
                        <div className="flex min-w-0 items-center gap-1.5 py-1">
                          <span className="truncate text-[13px] font-medium leading-tight">{party?.name ?? "—"}</span>
                          {(party?.cityName || party?.countryName) && (
                            <span className="shrink-0 text-[11px] text-muted-foreground">
                              {[party.cityName, party.countryName].filter(Boolean).join(" / ")}
                            </span>
                          )}
                        </div>
                      ) : (
                        <SearchableSelect
                          options={partySelectOptions}
                          value={stop.partyId || "__none__"}
                          onValueChange={value => updateStop(index, value === "__none__" ? "" : value)}
                          placeholder="Seleccionar parada..."
                          searchPlaceholder="Buscar party..."
                        />
                      )}
                    </div>
                    <div className="flex items-center">
                      {stop.kind === "intermediate" && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeIntermediateStop(index)}
                          title="Eliminar parada"
                        >
                          <Trash2Icon className="size-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {!isLast && <div className="ml-2.5 border-l-2 border-dashed border-border py-3" />}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
